import { addCalendarDays } from '../compliance-calendar/date-only';
import { qiCommentHasStrongIdentifier } from './comment-identifiers';
import { QI_FIELDS, type QiField, type QiGroup } from './manifest';
import type { QiValidationIssue, QiValue, QiCollectionMetadata } from './types';

/** Inputs are already tenant-local YYYY-MM-DD civil dates, not timestamps.
 * Reuse calendar-day arithmetic without applying a second timezone conversion.
 * Timestamp callers must first use dateOnlyInTz(instant, tenantTimezone).
 */
export function qiDay(value: string): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return NaN;
  const day = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(day) && addCalendarDays(value, 0) === value ? day / 86400000 : NaN;
}
export function qiShiftDay(value: string, days: number): string {
  const day = qiDay(value);
  if (!Number.isFinite(day)) throw new Error('invalid_qi_civil_date');
  if (!Number.isInteger(days)) throw new Error('invalid_qi_day_offset');
  return addCalendarDays(value, days);
}
/** Guide §6.1: 1/01/2023, never 01/01/2023. */
export function formatQiXlsxDate(value: string): string {
  if (!Number.isFinite(qiDay(value))) throw new Error('invalid_qi_civil_date');
  const [year, month, day] = value.split('-');
  return `${Number(day)}/${month}/${year}`;
}
const issue = (
  code: string,
  path: string,
  category: QiValidationIssue['category'] = 'INTEGRITY',
): QiValidationIssue => ({ code, path, category });
export function validateQiField(
  field: QiField,
  value: QiValue | undefined,
  requireCollected = false,
): QiValidationIssue[] {
  if (!value || value.state === 'not_collected')
    return requireCollected && !['comment', 'target_count', 'target_percent'].includes(field.kind)
      ? [issue('not_collected', field.code, 'POLICY')]
      : [];
  if (value.state !== 'measured') return [issue('invalid_value_state', field.code)];
  const v = value.value;
  if (field.kind === 'comment') {
    if (typeof v !== 'string' || v.length > 1000) return [issue('invalid_comment', field.code)];
    // I-2: lodged comments must be de-identified; strong identifier patterns refuse.
    return qiCommentHasStrongIdentifier(v) ? [issue('comment_identifier_blocked', field.code)] : [];
  }
  if (field.kind === 'date')
    return typeof v === 'string' && Number.isFinite(qiDay(v))
      ? []
      : [issue('invalid_date', field.code)];
  return typeof v === 'number' &&
    Number.isFinite(v) &&
    v >= 0 &&
    (field.kind === 'target_percent' ? v <= 100 : Number.isSafeInteger(v))
    ? []
    : [issue('invalid_number', field.code)];
}
/** Manual §§7–20,23. Stage and mode sums need not equal distinct-person totals. */
export function validateQiValues(
  values: Record<string, QiValue>,
  requireCollected = false,
): QiValidationIssue[] {
  const issues = QI_FIELDS.flatMap((f) => validateQiField(f, values[f.code], requireCollected));
  for (const code of Object.keys(values))
    if (!QI_FIELDS.some((f) => f.code === code)) issues.push(issue('unknown_field', code));
  const n = (code: string): number | undefined => {
    const v = values[code];
    return v?.state === 'measured' && typeof v.value === 'number' ? v.value : undefined;
  };
  const le = (child: string, parent: string) => {
    const c = n(child),
      p = n(parent);
    if (c !== undefined && p !== undefined && c > p)
      issues.push(issue('subset_exceeds_total', child));
  };
  // Manual §7 and Department T Section 1: "assessed" already excludes the
  // reported exclusions. Excluded can exceed assessed (e.g. 0 assessed/1 absent).
  // Subtracting them again or imposing excluded<=assessed rejects valid collections.
  const assessedBounds = (total: string, outcomes: string[]) => {
    for (const outcome of outcomes) le(outcome, total);
  };
  const seq = (g: string, a: number, b: number) =>
    Array.from({ length: b - a + 1 }, (_, i) => `${g}-${String(a + i).padStart(2, '0')}`);
  assessedBounds('PI-01', ['PI-04']);
  for (let i = 5; i <= 10; i++) le(`PI-${String(i).padStart(2, '0')}`, 'PI-04');
  le('PI-11', 'PI-04');
  for (let i = 12; i <= 17; i++) {
    le(`PI-${i}`, 'PI-11');
    le(`PI-${i}`, `PI-${String(i - 7).padStart(2, '0')}`);
  }
  assessedBounds('PR-02', ['PR-04']);
  le('PR-05', 'PR-04');
  assessedBounds('UPWL-01', ['UPWL-05']);
  assessedBounds('UPWL-08', ['UPWL-12']);
  assessedBounds('FMI-01', ['FMI-03']);
  le('FMI-04', 'FMI-03');
  assessedBounds('MM-02', ['MM-04']);
  assessedBounds('MM-08', ['MM-10']);
  le('MM-11', 'MM-10');
  assessedBounds('ADL-01', ['ADL-05', 'ADL-06']);
  const adl = [n('ADL-01'), n('ADL-05'), n('ADL-06')];
  if (
    adl.every((x) => x !== undefined) &&
    adl.slice(1).reduce<number>((s, x) => s + (x ?? 0), 0) > adl[0]!
  )
    issues.push(issue('adl_zero_and_decline_exceed_eligible', 'ADL-06'));
  assessedBounds('IAD-01', ['IAD-03', 'IAD-04']);
  le('IAD-05', 'IAD-04');
  const ic = [n('IAD-01'), n('IAD-03'), n('IAD-04')];
  if (ic.every((x) => x !== undefined) && ic[1]! + ic[2]! !== ic[0])
    issues.push(issue('incontinence_population_mismatch', 'IAD-04'));
  for (const c of seq('IAD', 6, 9)) le(c, 'IAD-05');
  assessedBounds('HP-01', ['HP-04']);
  le('HP-03', 'HP-04');
  for (let i = 1; i <= 4; i++) {
    le(`WF-${String(i + 4).padStart(2, '0')}`, `WF-${String(i).padStart(2, '0')}`);
    le(`WF-${String(i + 8).padStart(2, '0')}`, `WF-${String(i + 4).padStart(2, '0')}`);
  }
  for (const g of ['CEI', 'QOL']) {
    // Completed total excludes absent/opted out. Dual modes may repeat a person across modes.
    for (const c of seq(g, 4, 18)) le(c, `${g}-01`);
    for (let start = 4; start <= 14; start += 5) {
      const band = seq(g, start, start + 4).map(n),
        total = n(`${g}-01`);
      if (
        total !== undefined &&
        band.every((x) => x !== undefined) &&
        band.reduce<number>((s, x) => s + (x ?? 0), 0) > total
      )
        issues.push(issue('mode_exceeds_completed', `${g}-${start}`));
    }
  }
  assessedBounds('AH-01', []);
  le('AH-11', 'AH-03');
  for (let i = 4; i <= 10; i++) {
    le(`AH-${i + 8}`, `AH-${String(i).padStart(2, '0')}`);
    le(`AH-${String(i).padStart(2, '0')}`, 'AH-01');
  }
  for (const [total, start, end] of [
    ['AH-03', 4, 10],
    ['AH-11', 12, 18],
  ] as const) {
    const t = n(total),
      parts = seq('AH', start, end).map(n);
    if (
      t !== undefined &&
      parts.every((x) => x !== undefined) &&
      parts.reduce<number>((s, x) => s + (x ?? 0), 0) !== t
    )
      issues.push(issue('discipline_total_mismatch', total));
  }
  for (const [excluded, comment] of [
    ['UPWL-04', 'UPWL-06'],
    ['UPWL-11', 'UPWL-13'],
    ['ADL-04', 'ADL-07'],
  ] as const) {
    if ((n(excluded) ?? 0) > 0) {
      const text = values[comment];
      if (text?.state !== 'measured' || typeof text.value !== 'string' || !text.value.trim())
        issues.push(issue('missing_exclusion_comment', comment));
    }
  }
  return issues;
}

/** Collection dates are within quarter; FMI/HP/WF/AH review dates may be +21.
 * PR starts two days before its collection date, including before period start.
 */
export function validateQiCollectionMetadata(
  group: QiGroup,
  metadata: QiCollectionMetadata,
  start: string,
  end: string,
): QiValidationIssue[] {
  const issues: QiValidationIssue[] = [];
  for (const key of [
    'collectionDate',
    'windowStart',
    'windowEnd',
    'polypharmacyDate',
    'antipsychoticDate',
  ] as const) {
    const value = metadata[key];
    if (value === undefined) continue;
    const before = key === 'windowStart' ? (group === 'PR' ? 2 : group === 'MM' ? 6 : 0) : 0;
    const after = ['FMI', 'HP', 'WF', 'AH'].includes(group) ? 21 : 0;
    if (
      !Number.isFinite(qiDay(value)) ||
      qiDay(value) < qiDay(start) - before ||
      qiDay(value) > qiDay(end) + after
    )
      issues.push(issue('invalid_collection_date', group + '.' + key));
  }
  const last =
    group === 'PR'
      ? metadata.collectionDate
      : group === 'MM'
        ? metadata.antipsychoticDate
        : undefined;
  if (last && Number.isFinite(qiDay(last))) {
    if (
      (metadata.windowEnd && metadata.windowEnd !== last) ||
      (metadata.windowStart && metadata.windowStart !== qiShiftDay(last, group === 'PR' ? -2 : -6))
    )
      issues.push(issue('invalid_collection_window', group));
    if (group === 'MM' && qiDay(last) < qiDay(start) + 7)
      issues.push(issue('invalid_collection_window', group));
  }
  return issues;
}
